/**
 * F04 — session restore decision (cookie refresh → in-memory access token).
 * Does not invent a second auth system; reuses HttpOnly refresh + CSRF.
 */

export type SessionRestoreOutcome =
  | { readonly kind: "anonymous" }
  | { readonly kind: "restored"; readonly organizationId: string | null };

export interface SessionRestoreDeps {
  readonly readCsrfToken: () => string | null;
  readonly getAccessToken: () => string | null;
  readonly getOrganizationId: () => string | null;
  readonly refreshSession: () => Promise<unknown>;
  readonly clearReadableSessionArtifacts: () => void;
}

/**
 * Restore SPA memory session from existing secure cookies after hard reload.
 * - Memory already complete → restored (no network).
 * - No CSRF → anonymous (cannot refresh from SPA).
 * - Refresh OK → restored with org claim from JWT (may be null → org picker).
 * - Refresh fail → clear bad local state → anonymous (single attempt; no loop).
 */
export const attemptSessionRestore = async (
  deps: SessionRestoreDeps,
): Promise<SessionRestoreOutcome> => {
  if (deps.getAccessToken() !== null && deps.getOrganizationId() !== null) {
    return { kind: "restored", organizationId: deps.getOrganizationId() };
  }
  if (deps.readCsrfToken() === null) {
    deps.clearReadableSessionArtifacts();
    return { kind: "anonymous" };
  }
  try {
    await deps.refreshSession();
  } catch {
    deps.clearReadableSessionArtifacts();
    return { kind: "anonymous" };
  }
  return { kind: "restored", organizationId: deps.getOrganizationId() };
};
